<script setup>
// Картка прямого ефіру в каталозі. Свідомо окремий компонент, а не MyCard
// із синтетичним об'єктом: там картка зшита з полями аніме (серії, дата,
// оцінка, приватність), і підсовувати їй фейкове аніме означало б підганяти
// під неї дані, яких у стріму просто немає.
//
// Верстка обгортки повторює MyCard — інакше картка випадала б із сітки.
const props = defineProps({
  title: { type: String, default: 'Прямий ефір' },
  posterUrl: { type: String, default: null }
})

const poster = computed(() =>
  props.posterUrl ? useStrapiMedia(props.posterUrl) : null
)
</script>

<template>
  <!--
    Свідомо <a>, а не NuxtLink: на /live треба саме повне перезавантаження.
    Клієнтський перехід приносив сторінку напівживою — на телефоні від
    плеєра лишалась сама кнопка play. Сторінка все одно окрема: свій layout,
    свій плеєр і жодного спільного з каталогом стану, тож економити тут
    нічого. Те саме правило — у посиланні з навбару (MyNavbar) і в
    автоперекиданні (useLiveAutoRedirect)
  -->
  <a
    href="/live"
    class="group/card block isolate relative w-full"
  >
    <div
      class="relative shadow-md hover:shadow-2xl rounded-[12px] w-full overflow-hidden transition-shadow duration-500 mobile-aspect"
    >
      <NuxtImg
        v-if="poster"
        provider="ipx"
        :src="poster"
        format="webp"
        :alt="title"
        class="absolute inset-0 bg-base-100 w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-500 ease-out"
        quality="80"
        sizes="180px lg:241px"
        :densities="'1x'"
        placeholder
      />
      <!-- Постер не заповнили — щоб картка не була діркою в сітці -->
      <div
        v-else
        class="absolute inset-0 flex justify-center items-center bg-base-300"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          class="opacity-30"
        >
          <path
            fill="currentColor"
            d="M12 10a2 2 0 0 1 2 2a2 2 0 0 1-2 2a2 2 0 0 1-2-2a2 2 0 0 1 2-2m-5.5 2c0 1.93.9 3.65 2.3 4.77l-1.06 1.06A7.98 7.98 0 0 1 5 12c0-2.37 1.03-4.5 2.67-5.96l1.06 1.06A6.48 6.48 0 0 0 6.5 12m11 0c0-1.93-.9-3.65-2.3-4.77l1.06-1.06A7.98 7.98 0 0 1 19 12a7.98 7.98 0 0 1-2.74 6.03l-1.06-1.06A6.48 6.48 0 0 0 17.5 12"
          />
        </svg>
      </div>

      <div
        class="z-10 absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-300"
      />

      <!-- Плашка «Стрім» замість дати, як в аніме -->
      <div
        class="top-[10px] left-[10px] z-20 absolute flex items-center gap-[6px] bg-error/90 px-[8px] py-[3px] rounded-md font-semibold text-[11px] text-error-content uppercase tracking-wide"
      >
        <span class="relative flex w-2 h-2">
          <span
            class="inline-flex absolute bg-error-content opacity-75 rounded-full w-full h-full animate-ping"
          ></span>
          <span
            class="inline-flex relative bg-error-content rounded-full w-2 h-2"
          ></span>
        </span>
        Стрім
      </div>

      <div
        class="z-10 absolute inset-0 flex justify-center items-center opacity-0 group-hover/card:opacity-100 scale-75 group-hover/card:scale-100 transition-all duration-300"
      >
        <div
          class="flex justify-center items-center bg-primary shadow-lg rounded-full w-12 h-12 text-primary-content"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
          >
            <!-- Icon from MingCute Icon by MingCute Design - https://github.com/Richard9394/MingCute/blob/main/LICENSE -->
            <g
              fill="none"
              fill-rule="evenodd"
            >
              <path
                d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z"
              />
              <path
                fill="currentColor"
                d="M5.669 4.76a1.47 1.47 0 0 1 2.04-1.177c1.062.454 3.442 1.533 6.462 3.276c3.021 1.744 5.146 3.267 6.069 3.958c.788.591.79 1.763.001 2.356c-.914.687-3.013 2.19-6.07 3.956c-3.06 1.766-5.412 2.832-6.464 3.28c-.906.387-1.92-.2-2.038-1.177c-.138-1.142-.396-3.735-.396-7.237c0-3.5.257-6.092.396-7.235"
              />
            </g>
          </svg>
        </div>
      </div>
    </div>

    <div class="mt-[8px] font-medium text-sm line-clamp-2">
      {{ title }}
    </div>
    <div class="opacity-60 text-xs">Прямий ефір</div>
  </a>
</template>
